<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-dialog v-model="modal" :persistent="hasFilledFields" max-width="500px">
    <v-btn slot="activator" color="pink" dark small bottom left fab>
      <v-icon>add</v-icon>
    </v-btn>

    <v-card v-if="modal">
      <v-card-title>
        <span class="headline">{{ $language('pages.projects.modals.add.title') }}</span>
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs12 sm12 md12>
              <v-text-field
                :label="$language('pages.projects.modals.add.name')"
                v-model="form.Name"
                :rules="[required, notDuplicateName]"
                clearable
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm12 md12>
              <v-combobox
                :label="$language('pages.projects.modals.add.responsible')"
                v-model="form.Responsible"
                :rules="[]"
                clearable
                :items="responsible"
              ></v-combobox>
            </v-flex>

            <v-flex xs12 sm12 md12>
              <v-text-field
                :label="$language('pages.projects.modals.add.orderValue')"
                v-model="form.OrderValue"
                :rules="[required]"
                clearable
                @keypress="$helpers.isInteger"
                @keyup="form.OrderValue = form.OrderValue.toString().replace(/ /g,'').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')"
              ></v-text-field>
            </v-flex>

            <v-flex xs12 sm12 md12>
              <v-overflow-btn
                ref="ProjectCategoryID"
                v-model="form.ProjectCategoryID"
                :rules="[required]"
                :items="categories"
                item-text="Name"
                item-value="ID"
                clearable
                editable
                @blur="$set(focused, 'ProjectCategoryID', false)"
                @focus="$set(focused, 'ProjectCategoryID', true)"
              >
                <template v-slot:selection="{ item }">
                  {{ categories.find(category => category.ID === item).Name }}
                </template>
              </v-overflow-btn>
              <label
                class="v-label theme--light cursor-text"
                :class="{
                  'v-label--active': focused['ProjectCategoryID'] || form.ProjectCategoryID,
                  'v-label--full': !focused['ProjectCategoryID'] && !form.ProjectCategoryID,
                  'primary--text': focused['ProjectCategoryID'],
                }"
                :style="focused['ProjectCategoryID'] || form.ProjectCategoryID ? 'top: 4px; left: 4px; right: auto; position: absolute;' : ''"
                @click="$refs.ProjectCategoryID.onClick()"
              >{{ $language('pages.projects.modals.add.category') }}</label>
            </v-flex>

            <v-flex xs12 sm12 md12>
              <v-overflow-btn
                ref="DepartmentID"
                v-model="form.DepartmentID"
                :rules="[required]"
                :items="departments"
                item-text="Number"
                item-value="ID"
                clearable
                editable
                @blur="$set(focused, 'DepartmentID', false)"
                @focus="$set(focused, 'DepartmentID', true)"
              >
                <template v-slot:selection="{ item }">
                  {{ departments.find(department => department.ID === item).Number }}
                </template>
              </v-overflow-btn>
              <label
                class="v-label theme--light cursor-text"
                :class="{
                  'v-label--active': focused['DepartmentID'] || form.DepartmentID,
                  'v-label--full': !focused['DepartmentID'] && !form.DepartmentID,
                  'primary--text': focused['DepartmentID'],
                }"
                :style="focused['DepartmentID'] || form.DepartmentID ? 'top: 4px; left: 4px; right: auto; position: absolute;' : ''"
                @click="$refs.DepartmentID.onClick()"
              >{{ $language('pages.projects.modals.add.owner') }}</label>
            </v-flex>

            <v-flex xs12 sm12 md12>
              <v-overflow-btn
                :class="{ 'hide-clear-icon': !form.DepartmentIDs.length }"
                v-model="form.DepartmentIDs"
                :rules="[requiredArray]"
                :items="departments"
                item-text="Number"
                item-value="ID"
                multiple
                clearable
                editable
                @blur="$set(focused, 'DepartmentIDs', false)"
                @focus="$set(focused, 'DepartmentIDs', true)"
              >
                <template v-slot:selection="{ item }">
                  <label
                    class="v-label theme--light"
                    :class="{
                      'v-label--active': focused['DepartmentIDs'] || form.DepartmentIDs.length,
                      'primary--text': focused['DepartmentIDs'],
                    }"
                    :style="focused['DepartmentIDs'] || form.DepartmentIDs.length ? 'left: 0px; right: auto; position: absolute;' : ''"
                  >{{ $language('pages.projects.modals.add.departments') }}</label>
                  {{ form.DepartmentIDs ? departments
                  .filter(department => form.DepartmentIDs.includes(department.ID))
                  .map(department => department.Number)
                  .join(', ') : '' }}
                </template>
                <template v-slot:item="{ item }">
                  <v-checkbox color="primary" :value="form.DepartmentIDs.includes(item.ID)" :label="item.Number"/>
                </template>
              </v-overflow-btn>
            </v-flex>

            <v-flex xs12 sm12 md12 v-if="errorMessage">
              <p class="error-message">{{ errorMessage }}</p>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn
          @click.native="close"
          color="info"
          flat
        >{{ $language('pages.projects.modals.add.closeButton') }}</v-btn>

        <v-btn
          @click.native="create"
          :disabled="hasEmptyFields || hasNameDuplicate || $isLoading('create')"
          :loading="$isLoading('create')"
          color="info"
          flat
        >{{ $language('pages.projects.modals.add.createButton') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import loading from '@/mixin/loading';

export default {
  name: 'add-project-modal',
  mixins: [loading],
  props: {
    departments: { type: Array, required: true },
    projects: { type: Array, required: true },
    categories: { type: Array, required: true },
    responsible: { type: Array, required: true },
  },
  data() {
    return {
      form: {
        Name: '',
        Responsible: '',
        OrderValue: '',
        ProjectCategoryID: null,
        DepartmentID: null,
        DepartmentIDs: [],
      },
      //
      modal: false,
      errorMessage: '',
      focused: {},
    };
  },
  computed: {
    hasFilledFields: vm => Object.values(vm.form).some(value => (value instanceof Array ? value.length : value)),
    hasEmptyFields: (vm) => {
      const { Responsible, ...form } = vm.form;
      return Object.values(form).some(value => (value instanceof Array ? value.length === 0 : value === null || value === ''));
    },
    hasNameDuplicate: vm => !!vm.form.Name && !!vm.findProjectByName(vm.form.Name)
  },
  methods: {
    required(value) {
      return !!value || this.$language('validations.required');
    },
    requiredArray(value) {
      return !!(value && value.length) || this.$language('validations.required');
    },
    notDuplicateName(value) {
      return !value || !this.findProjectByName(value) || this.$language('validations.exists');
    },
    findProjectByName(name) {
      return this.projects.find(({ Name }) => Name.toLowerCase().trim() === name.toLowerCase().trim());
    },
    clear() {
      Object.entries(this.form).forEach(([field, value]) => {
        if (value instanceof Array) {
          this.$set(this.form, field, []);
        } else if (typeof value === 'string') {
          this.$set(this.form, field, '');
        } else {
          this.$set(this.form, field, null);
        }
      });
    },
    async create() {
      this.$startLoading('create');
      try {
        const project = await this.$api.projects.create({
          ...this.form,
          Responsible: this.form.Responsible || '',
          OrderValue: parseInt(this.form.OrderValue.replace(/ /g, ''), 10),
        });
        this.$emit('create', project);
        this.close(true);
      } catch (err) {
        console.log(err);
        this.errorMessage = (err.response && err.response.data && err.response.data.message) || err.message;
        this.$store.commit('snackbar/show', this.errorMessage);
      }
      this.$stopLoading('create');
    },
    close(force = false) {
      if (!force && this.hasFilledFields && !confirm('Are you sure you wan\'t to close the dialog?')) {
        return;
      }
      this.modal = false;
      this.clear();
      this.$stopLoading('create');
    },
  },
};
</script>

<style lang="scss" scoped>
  .flex {
    position: relative;
    .v-overflow-btn {
      /deep/ .v-select__slot {
        margin-bottom: -8px !important;
      }
      /deep/ .v-input__slot {
        box-shadow: none !important;
      }
      /deep/ .v-input__control:before {
        border-top: 1px solid white;
      }
      /deep/ .v-label {
        margin: 0;
      }
      /deep/ .v-select__slot input {
        margin-left: 0;
      }
    }
    & > .v-label {
      position: absolute;
      top: 0;
      z-index: 999999;
      font-size: 12px;
      margin-top: 10px;
      margin-bottom: 0;
      color: #757575;
    }
    &:focus-within {
      & > .v-label {
        color: #1876D2;
      }
    }
    .v-label--full {
      top: calc(50% - 20px) !important;
      font-size: 16px !important;
    }
    .hide-clear-icon {
      /deep/ .v-input__icon--clear {
        visibility: hidden !important;
      }
    }
    .error--text {
      /deep/ .v-label, & + .v-label {
        color: #FF5252 !important;
      }
    }
  }

  .error-message {
    color: #FF5252;
  }
</style>
